import ReactGA from "react-ga4";

// Initialize GA4
let isInitialized = false;

export const initGA = () => {
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  
  if (!measurementId) {
    console.warn("GA4 Measurement ID not found. Analytics will not be tracked.");
    return;
  }

  if (isInitialized) {
    console.warn("GA4 is already initialized.");
    return;
  }

  try {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        debug_mode: import.meta.env.DEV,
      },
    });
    isInitialized = true;
    console.log("GA4 initialized successfully");
  } catch (error) {
    console.error("Failed to initialize GA4:", error);
  }
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
  if (!isInitialized) return;
  
  try {
    ReactGA.send({ 
      hitType: "pageview", 
      page: path,
      title: title || document.title
    });
  } catch (error) {
    console.error("Failed to track page view:", error);
  }
};

// Track custom events
interface EventParams {
  [key: string]: any;
}

export const trackEvent = (
  eventName: string,
  params?: EventParams
) => {
  if (!isInitialized) return;

  try {
    ReactGA.event(eventName, params);
  } catch (error) {
    console.error(`Failed to track event ${eventName}:`, error);
  }
};

// Specific tracking functions for onboarding flow
export const trackOnboardingStarted = () => {
  trackEvent("onboarding_started", {
    step_number: 1,
  });
};

export const trackUserRegistrationCompleted = (userId: string, email: string) => {
  trackEvent("user_registration_completed", {
    step_number: 1,
    user_id: userId,
    email: email,
  });
};

export const trackUserRegistrationError = (errorMessage: string) => {
  trackEvent("user_registration_error", {
    step_number: 1,
    error_message: errorMessage,
  });
};

export const trackBusinessRegistrationStarted = (userId: string) => {
  trackEvent("business_registration_started", {
    step_number: 2,
    user_id: userId,
  });
};

export const trackBusinessRegistrationCompleted = (
  userId: string,
  businessId: string,
  businessName: string
) => {
  trackEvent("business_registration_completed", {
    step_number: 2,
    user_id: userId,
    business_id: businessId,
    business_name: businessName,
  });
};

export const trackBusinessRegistrationError = (
  userId: string,
  errorMessage: string
) => {
  trackEvent("business_registration_error", {
    step_number: 2,
    user_id: userId,
    error_message: errorMessage,
  });
};

export const trackStripeCheckoutView = (userId: string, businessId: string) => {
  trackEvent("stripe_checkout_view", {
    step_number: 3,
    user_id: userId,
    business_id: businessId,
  });
};

export const trackStripeCheckoutInitiated = (
  userId: string,
  businessId: string
) => {
  trackEvent("stripe_checkout_initiated", {
    step_number: 3,
    user_id: userId,
    business_id: businessId,
  });
};

export const trackStripeCheckoutError = (
  userId: string,
  businessId: string,
  errorMessage: string
) => {
  trackEvent("stripe_checkout_error", {
    step_number: 3,
    user_id: userId,
    business_id: businessId,
    error_message: errorMessage,
  });
};

export const trackOnboardingCompleted = (
  userId: string,
  businessId: string
) => {
  trackEvent("onboarding_completed", {
    user_id: userId,
    business_id: businessId,
  });
};

// Set user ID for tracking
export const setUserId = (userId: string) => {
  if (!isInitialized) return;
  
  try {
    ReactGA.set({ userId });
  } catch (error) {
    console.error("Failed to set user ID:", error);
  }
};

// Set user properties
export const setUserProperties = (properties: EventParams) => {
  if (!isInitialized) return;
  
  try {
    ReactGA.set(properties);
  } catch (error) {
    console.error("Failed to set user properties:", error);
  }
};

